import { createContext, useContext, useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useCachedApi, clearApiCache } from '../hooks/useCachedApi';

const imageUrlCache = new Map();

const defaultUserStatus = {
  planTier: 'free',
  creatorCredits: 0,
  profileCount: 0,
  creationCount: 0,
  maxProfiles: 0,
  maxCreations: 0,
  isProfileComplete: false,
  digitalLegacyPersonaId: null,
  termsAcceptedVersion: null,
};
const UserStatusContext = createContext(null);

export const UserStatusProvider = ({ children }) => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [userStatus, setUserStatus] = useState(defaultUserStatus);
  const { data: initialStatus, isLoading: isStatusLoading } = useCachedApi(
    isAuthenticated ? '/v1/user/auth/status' : null,
    60000
  );

  useEffect(() => {
    if (initialStatus) {
      setUserStatus(initialStatus);
    }
  }, [initialStatus]);

  const refreshUserStatus = async () => {
    if (isAuthenticated) {
      try {
        clearApiCache('/v1/user/auth/status');

        const accessToken = await getAccessTokenSilently();
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/v1/user/auth/status`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
          }
        );
        if (!response.ok) throw new Error('Failed to refresh status');
        const newStatus = await response.json();
        setUserStatus(newStatus);
      } catch (error) {
        console.error('Error refreshing user status:', error);
      }
    }
  };

  const bustImageCache = (key) => {
    if (imageUrlCache.has(key)) {
      imageUrlCache.delete(key);
    }
  };

  const value = {
    userStatus,
    setUserStatus,
    refreshUserStatus,
    imageUrlCache,
    bustImageCache,
    isLoading: isStatusLoading,
  };

  return (
    <UserStatusContext.Provider value={value}>
      {children}
    </UserStatusContext.Provider>
  );
};

export const useUserStatus = () => {
  return useContext(UserStatusContext);
};
